import { useEffect, useMemo, useState } from "react";
import WeeklyDriverRecordsTable from "./WeeklyDriverRecordsTable";
import DriverIntelligenceCharts from "./DriverIntelligenceCharts";
import { Eye, RefreshCw } from "lucide-react";

import useReports from "../../Sevices/useReports";
import LoadingScreen from "../Utils/LoadingScreen";
import { toast } from "sonner";

const safePercent = (part, total) =>
  Number(total || 0) > 0 ? (Number(part || 0) / Number(total || 0)) * 100 : 0;

const SectionInfo = ({ title, description, dark = false, className = "" }) => {
  return (
    <div className={`group relative shrink-0 ${className}`}>
      <button
        type="button"
        aria-label={`About ${title}`}
        className={`flex h-7 w-7 items-center justify-center rounded-md border transition ${
          dark
            ? "border-white/20 bg-white/10 text-white hover:bg-white/20"
            : "border-gray-200 bg-white text-gray-500 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-600"
        }`}
      >
        <Eye size={13} />
      </button>

      <div className="pointer-events-none invisible absolute right-0 top-full z-[150] mt-1.5 w-[290px] max-w-[calc(100vw-24px)] translate-y-1 rounded-lg border border-gray-200 bg-white p-2.5 text-left opacity-0 shadow-xl transition-all duration-150 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
        <p className="text-xs font-black text-gray-900">{title}</p>
        <p className="mt-1 text-[10px] leading-4 text-gray-600">
          {description}
        </p>
      </div>
    </div>
  );
};

const DriverIntelligenceTab = ({
  initialFromDate,
  initialToDate,
  drivers = [],
  viewMode = "table",
}) => {
  const today = new Date().toISOString().split("T")[0];
  const { fetchWeeklyDriverReport, weeklyDriverReportLoading } = useReports();

  const [fromDate, setFromDate] = useState(initialFromDate || today);
  const [toDate, setToDate] = useState(initialToDate || today);
  const [reportData, setReportData] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const rowsPerPage = 15;

  const driverDirectory = useMemo(() => {
    const map = new Map();

    (drivers || []).forEach((driver) => {
      const code = String(driver?.driverId || "").trim();
      if (!code) return;

      const fullName =
        driver?.fullName?.trim() ||
        [driver?.first_name, driver?.last_name]
          .filter(Boolean)
          .join(" ")
          .trim() ||
        code;

      map.set(code, {
        ...driver,
        fullName,
      });
    });

    return map;
  }, [drivers]);

  const getDriverMeta = (driverCode) => {
    const code = String(driverCode || "").trim();
    const driver = driverDirectory.get(code);

    return {
      driverName: driver?.fullName || code || "Unknown Driver",
      teamName: driver?.teamName || "Unassigned",
      phone: driver?.phone || "-",
      status: driver?.status || "unknown",
      dutyStatus: driver?.dutyStatus || "unknown",
      matched: Boolean(driver),
    };
  };

  const loadWeeklyReport = async () => {
    if (!fromDate || !toDate) {
      toast.error("Please select from date and to date");
      return;
    }

    if (new Date(`${fromDate}T00:00:00`) > new Date(`${toDate}T00:00:00`)) {
      toast.error("From date cannot be greater than to date");
      return;
    }

    await fetchWeeklyDriverReport(fromDate, toDate, setReportData);
    setCurrentPage(1);
  };

  useEffect(() => {
    loadWeeklyReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const weeklyRows = useMemo(() => {
    if (!Array.isArray(reportData)) return [];

    return reportData.flatMap((driver) => {
      const driverCode = driver?.driver_code || "N/A";
      const meta = getDriverMeta(driverCode);
      const reports = Array.isArray(driver?.weeklyReport)
        ? driver.weeklyReport
        : [];

      return reports.map((week, index) => {
        const assignedTasks = Number(week?.assigned_tasks || 0);
        const successfulTasks = Number(week?.successful_tasks || 0);
        const declinedTasks = Number(week?.declined_tasks || 0);
        const pendingTasks = Math.max(
          assignedTasks - successfulTasks - declinedTasks,
          0,
        );

        const totalWeekAmount = Number(week?.total_week_amount || 0);
        const totalPerOrderAmount = Number(week?.total_per_order_amount || 0);
        const totalExtraAmount = Number(week?.total_extra_amount || 0);
        const reportedAmount =
          totalWeekAmount + totalPerOrderAmount + totalExtraAmount;

        return {
          id: `${driverCode}-${week?.week_start || index}-${week?.week_end || index}`,
          driverCode,
          ...meta,
          weekStart: week?.week_start || "-",
          weekEnd: week?.week_end || "-",
          assignedTasks,
          successfulTasks,
          declinedTasks,
          pendingTasks,
          successRate: safePercent(successfulTasks, assignedTasks),
          declineRate: safePercent(declinedTasks, assignedTasks),
          totalWeekAmount,
          totalPerOrderAmount,
          totalExtraAmount,
          reportedAmount,
        };
      });
    });
  }, [reportData, driverDirectory]);

  const driverSummary = useMemo(() => {
    const grouped = new Map();

    weeklyRows.forEach((row) => {
      if (!grouped.has(row.driverCode)) {
        grouped.set(row.driverCode, {
          driverCode: row.driverCode,
          driverName: row.driverName,
          teamName: row.teamName,
          phone: row.phone,
          status: row.status,
          dutyStatus: row.dutyStatus,
          matched: row.matched,
          assigned: 0,
          successful: 0,
          declined: 0,
          pending: 0,
          totalWeekAmount: 0,
          totalPerOrderAmount: 0,
          totalExtraAmount: 0,
          reportedAmount: 0,
          weeks: 0,
          activeWeeks: 0,
        });
      }

      const item = grouped.get(row.driverCode);
      item.assigned += row.assignedTasks;
      item.successful += row.successfulTasks;
      item.declined += row.declinedTasks;
      item.pending += row.pendingTasks;
      item.totalWeekAmount += row.totalWeekAmount;
      item.totalPerOrderAmount += row.totalPerOrderAmount;
      item.totalExtraAmount += row.totalExtraAmount;
      item.reportedAmount += row.reportedAmount;
      item.weeks += 1;
      if (row.assignedTasks > 0) item.activeWeeks += 1;
    });

    return Array.from(grouped.values())
      .map((item) => ({
        ...item,
        successRate: safePercent(item.successful, item.assigned),
        declineRate: safePercent(item.declined, item.assigned),
        avgTasksPerActiveWeek:
          item.activeWeeks > 0 ? item.assigned / item.activeWeeks : 0,
        amountPerSuccessfulTask:
          item.successful > 0 ? item.reportedAmount / item.successful : 0,
      }))
      .sort((a, b) => b.assigned - a.assigned);
  }, [weeklyRows]);

  const weekSummary = useMemo(() => {
    const grouped = new Map();

    weeklyRows.forEach((row) => {
      const key = `${row.weekStart}|${row.weekEnd}`;

      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          weekStart: row.weekStart,
          weekEnd: row.weekEnd,
          assigned: 0,
          successful: 0,
          declined: 0,
          pending: 0,
          reportedAmount: 0,
          driverCodes: new Set(),
        });
      }

      const week = grouped.get(key);
      week.assigned += row.assignedTasks;
      week.successful += row.successfulTasks;
      week.declined += row.declinedTasks;
      week.pending += row.pendingTasks;
      week.reportedAmount += row.reportedAmount;
      if (row.assignedTasks > 0) week.driverCodes.add(row.driverCode);
    });

    return Array.from(grouped.values())
      .map((week) => ({
        ...week,
        activeDrivers: week.driverCodes.size,
        successRate: safePercent(week.successful, week.assigned),
        declineRate: safePercent(week.declined, week.assigned),
      }))
      .sort((a, b) => String(a.weekStart).localeCompare(String(b.weekStart)));
  }, [weeklyRows]);

  const totals = useMemo(() => {
    return weeklyRows.reduce(
      (acc, row) => {
        acc.assigned += row.assignedTasks;
        acc.successful += row.successfulTasks;
        acc.declined += row.declinedTasks;
        acc.pending += row.pendingTasks;
        acc.totalWeekAmount += row.totalWeekAmount;
        acc.totalPerOrderAmount += row.totalPerOrderAmount;
        acc.totalExtraAmount += row.totalExtraAmount;
        acc.reportedAmount += row.reportedAmount;
        return acc;
      },
      {
        assigned: 0,
        successful: 0,
        declined: 0,
        pending: 0,
        totalWeekAmount: 0,
        totalPerOrderAmount: 0,
        totalExtraAmount: 0,
        reportedAmount: 0,
      },
    );
  }, [weeklyRows]);

  const activeDrivers = driverSummary.filter((driver) => driver.assigned > 0);
  const inactiveDrivers = driverSummary.filter(
    (driver) => driver.assigned === 0,
  );
  const mappedDrivers = driverSummary.filter((driver) => driver.matched).length;

  const overallSuccessRate = safePercent(totals.successful, totals.assigned);
  const overallDeclineRate = safePercent(totals.declined, totals.assigned);
  const avgTasksPerActiveDriver = activeDrivers.length
    ? totals.assigned / activeDrivers.length
    : 0;
  const amountPerSuccessfulTask = totals.successful
    ? totals.reportedAmount / totals.successful
    : 0;
  const driverMappingRate = driverSummary.length
    ? safePercent(mappedDrivers, driverSummary.length)
    : 0;

  const topVolumeDriver = driverSummary[0] || null;
  const eligiblePerformanceDrivers = driverSummary.filter(
    (driver) => driver.assigned >= 5,
  );
  const bestSuccessDriver = [...eligiblePerformanceDrivers].sort(
    (a, b) => b.successRate - a.successRate || b.assigned - a.assigned,
  )[0];
  const highestDeclineDriver = [...eligiblePerformanceDrivers].sort(
    (a, b) => b.declineRate - a.declineRate || b.declined - a.declined,
  )[0];

  const attentionDrivers = driverSummary
    .filter(
      (driver) =>
        driver.assigned >= 5 &&
        (driver.successRate < 75 || driver.declineRate >= 25),
    )
    .sort(
      (a, b) => b.declineRate - a.declineRate || a.successRate - b.successRate,
    );

  const zeroAmountSuccessRows = weeklyRows.filter(
    (row) => row.successfulTasks > 0 && row.reportedAmount === 0,
  );

  const weekBucketsOutsideFilter = weekSummary.filter(
    (week) => week.weekEnd < fromDate || week.weekStart > toDate,
  );

  const top3TaskShare = totals.assigned
    ? safePercent(
        driverSummary
          .slice(0, 3)
          .reduce((sum, driver) => sum + driver.assigned, 0),
        totals.assigned,
      )
    : 0;

  const performanceBands = useMemo(() => {
    return activeDrivers.reduce(
      (acc, driver) => {
        if (driver.successRate >= 90) acc.excellent += 1;
        else if (driver.successRate >= 75) acc.good += 1;
        else if (driver.successRate >= 60) acc.watch += 1;
        else acc.critical += 1;
        return acc;
      },
      { excellent: 0, good: 0, watch: 0, critical: 0 },
    );
  }, [driverSummary]);

  const previousWeek = weekSummary.length >= 2 ? weekSummary.at(-2) : null;
  const latestWeek = weekSummary.length >= 1 ? weekSummary.at(-1) : null;

  const weekOverWeek = useMemo(() => {
    if (!previousWeek || !latestWeek) return null;

    return {
      assignedDelta: latestWeek.assigned - previousWeek.assigned,
      successfulDelta: latestWeek.successful - previousWeek.successful,
      declinedDelta: latestWeek.declined - previousWeek.declined,
      successRateDelta: latestWeek.successRate - previousWeek.successRate,
      amountDelta: latestWeek.reportedAmount - previousWeek.reportedAmount,
    };
  }, [previousWeek, latestWeek]);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return weeklyRows;

    return weeklyRows.filter((row) =>
      [
        row.driverName,
        row.driverCode,
        row.teamName,
        row.phone,
        row.weekStart,
        row.weekEnd,
      ].some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(query),
      ),
    );
  }, [weeklyRows, search]);

  const totalCount = filteredRows.length;
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentRows = filteredRows.slice(startIndex, startIndex + rowsPerPage);

  useEffect(() => {
    const pages = Math.max(1, Math.ceil(totalCount / rowsPerPage));
    if (currentPage > pages) setCurrentPage(pages);
  }, [currentPage, totalCount]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(Number(value || 0));

  const assignmentActivityData = {
    labels: [
      "Assigned Attempts",
      "Successful Handles",
      "Declined Attempts",
      "Pending / Other",
    ],
    datasets: [
      {
        label: "Driver task records",
        data: [
          totals.assigned,
          totals.successful,
          totals.declined,
          totals.pending,
        ],
        backgroundColor: ["#f97316", "#16a34a", "#dc2626", "#f59e0b"],
        borderRadius: 7,
        maxBarThickness: 42,
      },
    ],
  };

  const performanceBandData = {
    labels: ["Excellent 90%+", "Good 75-89%", "Watch 60-74%", "Critical <60%"],
    datasets: [
      {
        data: [
          performanceBands.excellent,
          performanceBands.good,
          performanceBands.watch,
          performanceBands.critical,
        ],
        backgroundColor: ["#16a34a", "#0ea5e9", "#f59e0b", "#dc2626"],
        borderWidth: 0,
      },
    ],
  };

  const amountMixData = {
    labels: ["Week Amount", "Per Order Amount", "Extra Amount"],
    datasets: [
      {
        data: [
          totals.totalWeekAmount,
          totals.totalPerOrderAmount,
          totals.totalExtraAmount,
        ],
        backgroundColor: ["#7c3aed", "#2563eb", "#f59e0b"],
        borderWidth: 0,
      },
    ],
  };

  const weeklyTrendData = {
    labels: weekSummary.map((week) => `${week.weekStart} → ${week.weekEnd}`),
    datasets: [
      {
        label: "Assigned",
        data: weekSummary.map((week) => week.assigned),
        borderColor: "#f97316",
        backgroundColor: "#f97316",
        borderWidth: 3,
        pointRadius: 4,
        tension: 0.3,
      },
      {
        label: "Successful",
        data: weekSummary.map((week) => week.successful),
        borderColor: "#16a34a",
        backgroundColor: "#16a34a",
        borderWidth: 3,
        pointRadius: 4,
        tension: 0.3,
      },
      {
        label: "Declined",
        data: weekSummary.map((week) => week.declined),
        borderColor: "#dc2626",
        backgroundColor: "#dc2626",
        borderWidth: 3,
        pointRadius: 4,
        tension: 0.3,
      },
    ],
  };

  // Keep a unique driver code inside each chart label so tooltip data can
  // always resolve the exact row, even when two drivers have the same name.
  const DRIVER_LABEL_SEPARATOR = "|||DRIVER|||";

  const allDriverOutcomeLabels = driverSummary.map(
    (driver) =>
      `${driver.driverName}${DRIVER_LABEL_SEPARATOR}${driver.driverCode}`,
  );

  const getDriverFromOutcomeLabel = (label = "") => {
    const rawLabel = String(label || "");
    const separatorIndex = rawLabel.lastIndexOf(DRIVER_LABEL_SEPARATOR);

    if (separatorIndex === -1) return null;

    const driverCode = rawLabel
      .slice(separatorIndex + DRIVER_LABEL_SEPARATOR.length)
      .trim();

    return (
      driverSummary.find(
        (driver) => String(driver?.driverCode || "").trim() === driverCode,
      ) || null
    );
  };

  const getVisibleOutcomeDriverName = (label = "") => {
    const rawLabel = String(label || "");
    const separatorIndex = rawLabel.lastIndexOf(DRIVER_LABEL_SEPARATOR);

    return separatorIndex === -1 ? rawLabel : rawLabel.slice(0, separatorIndex);
  };

  const allDriverOutcomeData = {
    labels: allDriverOutcomeLabels,
    datasets: [
      {
        label: "Successful",
        data: driverSummary.map((driver) => driver.successful),
        backgroundColor: "#16a34a",
        stack: "tasks",
      },
      {
        label: "Declined",
        data: driverSummary.map((driver) => driver.declined),
        backgroundColor: "#dc2626",
        stack: "tasks",
      },
      {
        label: "Pending / Other",
        data: driverSummary.map((driver) => driver.pending),
        backgroundColor: "#f59e0b",
        stack: "tasks",
      },
    ],
  };

  const successRateData = {
    labels: driverSummary.map((driver) => driver.driverName),
    datasets: [
      {
        label: "Success Rate",
        data: driverSummary.map((driver) =>
          Number(driver.successRate.toFixed(1)),
        ),
        backgroundColor: driverSummary.map((driver) => {
          if (driver.assigned === 0) return "#cbd5e1";
          if (driver.successRate >= 90) return "#16a34a";
          if (driver.successRate >= 75) return "#0ea5e9";
          if (driver.successRate >= 60) return "#f59e0b";
          return "#dc2626";
        }),
        borderRadius: 5,
        maxBarThickness: 22,
      },
    ],
  };

  const driverChartHeight = Math.max(driverSummary.length * 36 + 90, 380);

  if (weeklyDriverReportLoading && reportData.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-3">
      {/* Compact report controls */}
      <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
        <div className="flex flex-col gap-2 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-black text-gray-900">
                {viewMode === "table"
                  ? "Driver Weekly Detail"
                  : "Weekly Driver Intelligence"}
              </h2>
              <span className="rounded-full bg-orange-50 px-2 py-0.5 text-[9px] font-bold text-orange-700">
                {fromDate} → {toDate}
              </span>
              <SectionInfo
                title={
                  viewMode === "table"
                    ? "Driver Weekly Detail"
                    : "Weekly Driver Intelligence"
                }
                description={
                  viewMode === "table"
                    ? "This view shows auditable driver-week rows returned by the weekly report API. Use the date filters to reload the report, then inspect task outcomes, derived rates and all three amount fields for each driver-week."
                    : "This view converts the weekly report API into admin analytics. The selected From/To dates are sent to the API; KPI cards, trends, driver comparisons, diagnostics and amount analysis are all recalculated from the returned rows."
                }
              />
            </div>
            <p className="mt-0.5 max-w-3xl text-[11px] leading-4 text-gray-500">
              {viewMode === "table"
                ? "Auditable driver-week records with task outcomes, success rate and every amount field returned by the API."
                : "Power BI-style operational view built only from the weekly driver report API: volume, efficiency, risk, trend, workload and reported payout fields."}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[145px_145px_auto] sm:items-end">
            <div>
              <label className="mb-1 block text-[11px] font-semibold text-gray-600">
                From Date
              </label>
              <input
                type="date"
                value={fromDate}
                max={toDate || today}
                onChange={(event) => setFromDate(event.target.value)}
                className="h-9 w-full rounded-lg border border-gray-200 px-2.5 text-xs outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-[11px] font-semibold text-gray-600">
                To Date
              </label>
              <input
                type="date"
                value={toDate}
                min={fromDate}
                max={today}
                onChange={(event) => setToDate(event.target.value)}
                className="h-9 w-full rounded-lg border border-gray-200 px-2.5 text-xs outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              />
            </div>

            <button
              type="button"
              onClick={loadWeeklyReport}
              disabled={weeklyDriverReportLoading}
              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-orange-500 px-4 text-xs font-bold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw
                size={14}
                className={weeklyDriverReportLoading ? "animate-spin" : ""}
              />
              {weeklyDriverReportLoading ? "Loading..." : "Apply"}
            </button>
          </div>
        </div>
      </div>

      {viewMode === "chart" && (
        <DriverIntelligenceCharts
          totals={totals}
          activeDrivers={activeDrivers}
          overallSuccessRate={overallSuccessRate}
          overallDeclineRate={overallDeclineRate}
          avgTasksPerActiveDriver={avgTasksPerActiveDriver}
          amountPerSuccessfulTask={amountPerSuccessfulTask}
          driverMappingRate={driverMappingRate}
          mappedDrivers={mappedDrivers}
          driverSummary={driverSummary}
          weekSummary={weekSummary}
          top3TaskShare={top3TaskShare}
          attentionDrivers={attentionDrivers}
          topVolumeDriver={topVolumeDriver}
          bestSuccessDriver={bestSuccessDriver}
          highestDeclineDriver={highestDeclineDriver}
          inactiveDrivers={inactiveDrivers}
          latestWeek={latestWeek}
          weekOverWeek={weekOverWeek}
          zeroAmountSuccessRows={zeroAmountSuccessRows}
          weekBucketsOutsideFilter={weekBucketsOutsideFilter}
          fromDate={fromDate}
          toDate={toDate}
          assignmentActivityData={assignmentActivityData}
          weeklyTrendData={weeklyTrendData}
          driverChartHeight={driverChartHeight}
          allDriverOutcomeData={allDriverOutcomeData}
          getDriverFromOutcomeLabel={getDriverFromOutcomeLabel}
          getVisibleOutcomeDriverName={getVisibleOutcomeDriverName}
          successRateData={successRateData}
          performanceBandData={performanceBandData}
          amountMixData={amountMixData}
          formatCurrency={formatCurrency}
        />
      )}

      {viewMode === "table" && (
        <WeeklyDriverRecordsTable
          filteredRows={filteredRows}
          driverSummary={driverSummary}
          search={search}
          setSearch={setSearch}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          weeklyDriverReportLoading={weeklyDriverReportLoading}
          reportData={reportData}
          currentRows={currentRows}
          startIndex={startIndex}
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          formatCurrency={formatCurrency}
        />
      )}
    </div>
  );
};

export default DriverIntelligenceTab;
